/* eslint-disable */
import { createRoutine } from 'redux-saga-routines'
import {
  actionChannel,
  call,
  cancelled,
  delay,
  flush,
  fork,
  put,
  select,
  take,
  takeLatest,
  takeEvery
} from '@redux-saga/core/effects'
import { dissoc, isEmpty, pathOr, propOr } from 'ramda'
import * as bpService from 'services/BpService'
import * as fileService from 'services/fileService'
import { toast } from 'react-toastify'
import * as unitService from 'services/unitsService'
import { isNotNilOrEmpty } from 'utils/ramda'
import { fileToUploadPath, thumbnailToUploadPath } from 'utils/files'
import { fetchUnitRoutine } from 'ducks/units/actions'
import { getApiErrors } from 'utils/errors'
import { fetchBpRoutine } from 'features/bpDetails/ducks/actions'
import { isOnline as checkIsOnline } from 'ducks/offline/actions'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { fileUpdateMessage } from 'utils/fileUpdateMessage'

export const extendSessionRoutine = createRoutine('EXTEND_SESSION')
export const getBpFilesRoutine = createRoutine('GET_BP_FILES')
export const getBpFilesCountsRoutine = createRoutine('GET_BP_FILES_COUNTS')
export const getFilesStatisticsRoutine = createRoutine('GET_FILES_STATISTICS')
export const loadFilesByFolderRoutine = createRoutine('LOAD_FILES_BY_FOLDER')
export const loadFilesByCategoryRoutine = createRoutine('LOAD_FILES_BY_CATEGORY')
export const loadAllFilesRoutine = createRoutine('LOAD_ALL_FILES')
export const loadCustomFolderFilesRoutine = createRoutine('LOAD_CUSTOM_FOLDER_FILES')
export const invalidateCustomFolderCacheRoutine = createRoutine('INVALIDATE_CUSTOM_FOLDER_CACHE')
export const updateLoadingProgressRoutine = createRoutine('UPDATE_LOADING_PROGRESS')
export const cancelFileLoadingRoutine = createRoutine('CANCEL_FILE_LOADING')
export const cancelAutoViewLoadingRoutine = createRoutine('CANCEL_AUTO_VIEW_LOADING')

export const getRecentFilesRoutine = createRoutine('GET_RECENT_FILES')
export const getFileTypesRoutine = createRoutine('GET_FILE_TYPES')
export const finalizeFileRoutine = createRoutine('FINALIZE_FILE')
export const updateFileRoutine = createRoutine('UPDATE_FILE')
export const shareFileRoutine = createRoutine('SHARE_FILE')
export const downloadFileRoutine = createRoutine('DOWNLOAD_FILE')
export const removeFileRoutine = createRoutine('REMOVE_FILE')
export const bulkRemoveFilesRoutine = createRoutine('BULK_REMOVE_FILES')
export const restoreFileRoutine = createRoutine('RESTORE_FILE')
export const clearBpFilesRoutine = createRoutine('CLEAR_BP_FILES')
export const getSharedUnitsListForFileRoutine = createRoutine(
  'GET_SHARED_UNITS_FOR_FILE'
)
export const clearSharedUnitsListForFileRoutine = createRoutine(
  'CLEAR_SHARED_UNITS_FOR_FILE'
)
export const addFileVersionRoutine = createRoutine('ADD_FILE_VERSION')
export const getUploadProcessRoutine = createRoutine('GET_UPLOAD_PROCESS')
export const clearUploadProcessRoutine = createRoutine('CLEAR_UPLOAD_PROCESS')
export const saveUploadProcessFetchDetailsRoutine = createRoutine(
  'SAVE_UPLOAD_PROCESS'
)
export const clearUploadProcessFetchDetailsRoutine = createRoutine(
  'CLEAR_UPLOAD_PROCESS_DETAILS'
)

export const getFileTagsForBpRoutine = createRoutine('GET_FILE_TAGS_FOR_BP')
export const createFileTagRoutine = createRoutine('CREATE_FILE_TAG')
export const removeFileTagRoutine = createRoutine('REMOVE_FILE_TAG')
export const attachFileTagToFileRoutine = createRoutine('ATTACH_FILE_TAG_TO_BP')
export const syncFileTagsInFileRoutine = createRoutine('SYNC_FILE_TAGS')
export const syncFileTagsInFileVersionRoutine = createRoutine(
  'SYNC_FILE_VERSION_TAGS'
)
export const detachFileTagFromFileRoutine = createRoutine(
  'DETACH_FILE_TAG_FROM_BP'
)

export const reclassifyFileRoutine = createRoutine('RECLASSIFY_FILE')
export const getRecommendedFileTypesRoutine = createRoutine(
  'GET_RECOMMENDED_FILE_TYPES'
)

// Separate action for thumbnail updates - only updates bpFilesList without side effects
export const updateFileThumbnailsRoutine = createRoutine('UPDATE_FILE_THUMBNAILS')
export const requestFileThumbnailsRoutine = createRoutine('REQUEST_FILE_THUMBNAILS')

function* extendSessionSaga() {
  try {
    yield call(fileService.extendSession)
    yield put(extendSessionRoutine.success())
  } catch (error) {
    console.error('Session extension failed', error)
    yield put(extendSessionRoutine.failure(error))
  }
}

function* uploadFileVersion({ payload }) {
  const {
    bpId,
    unitId,
    uploadedImage,
    objectID,
    type,
    producedBy,
    creationDate,
    isFloorPlan,
    fileTitle,
    legalOwner,
    expirationDate,
    expirationReminderDate,
    approvedBy,
    segment,
    dateOfIssue,
    fileId,
    previousVersion,
    callback
  } = payload
  yield put(addFileVersionRoutine.request())
  try {
    const result = yield call(bpService.uploadFile, {
      uploadedImage,
      isFloorPlan,
      objectID,
      type
    })
    const path = propOr('', 'universalPath', result)
    if (type === 'building_unit_passport_file') {
      yield call(unitService.addUnitFileVersion, {
        fileId,
        bpId,
        unitId,
        previousVersion,
        path: fileToUploadPath(path, isFloorPlan),
        thumbnailPath: isFloorPlan ? thumbnailToUploadPath(path, true) : null,
        name: isNotNilOrEmpty(fileTitle)
          ? fileTitle
          : pathOr('', ['original_file_name'], result),
        producedBy,
        creationDate,
        legalOwner,
        expirationDate,
        expirationReminderDate,
        approvedBy,
        segment,
        dateOfIssue
      })
      yield put(fetchUnitRoutine({ bpId: bpId, unitId: objectID }))
    } else {
      const response = yield call(bpService.addFileVersion, {
        id: fileId,
        previousVersion,
        path: fileToUploadPath(path, isFloorPlan),
        thumbnailPath: isFloorPlan ? thumbnailToUploadPath(path, true) : null,
        name: isNotNilOrEmpty(fileTitle)
          ? fileTitle
          : pathOr('', ['original_file_name'], result),
        producedBy,
        creationDate,
        legalOwner,
        expirationDate,
        expirationReminderDate,
        approvedBy,
        segment,
        dateOfIssue
      })

      // Optimistic update: use the updated file from the response if available
      const updatedFile = pathOr(null, ['data', 'data'], response)
      if (updatedFile) {
        // Get current files list to update the specific file
        const currentFiles = yield select(state => state.files.bpFilesList)
        const updatedFiles = currentFiles.map(file =>
          file.id === updatedFile.id ? updatedFile : file
        )
        yield put(getBpFilesRoutine.success(updatedFiles))
      } else {
        // Fallback to full refetch if response doesn't contain file data
        yield put(getBpFilesRoutine({ id: bpId }))
      }
    }
    yield put(addFileVersionRoutine.success())
    toast.success('Adding a file...')
    callback()
  } catch (error) {
    yield put(addFileVersionRoutine.failure(error))
    if (checkIsOnline()) toast.error('Something went wrong')
  }
}

export function* removeFile({ payload }) {
  yield put(removeFileRoutine.request())
  const { id, bpId, callback } = payload
  try {
    yield call(bpService.removeFile, { id })
    // Optimistic update: the reducer handles removing the file from bpFilesList
    yield put(removeFileRoutine.success(id))
    toast.success('File has been deleted')
    // Still need to refetch BP data for updated statistics
    yield put(fetchBpRoutine({ id: bpId }))
    if (bpId) yield put(getBpFilesCountsRoutine({ id: bpId }))
    if (callback) {
      callback()
    }
  } catch (error) {
    yield put(removeFileRoutine.failure(error))
  }
}

function* restoreFile({ payload }) {
  yield put(restoreFileRoutine.request())
  const { bpId, fileId, callback } = payload
  try {
    yield call(bpService.restoreFile, { bpId, fileId })
    yield put(restoreFileRoutine.success())
    toast.success('File has been restored')
    yield put(fetchBpRoutine({ id: bpId }))
    yield put(getBpFilesRoutine({ id: bpId }))
    yield put(getBpFilesCountsRoutine({ id: bpId }))
    if (callback) {
      callback()
    }
  } catch (error) {
    toast.error('Failed to restore file')
    yield put(restoreFileRoutine.failure(error))
  }
}

// Helper to normalize UUID for matching (axios-case-converter mangles UUID keys)
function normalizeUuid(str) {
  return str.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
}

function* watchVisibleThumbnailRequests() {
  const requestChannel = yield actionChannel(requestFileThumbnailsRoutine.TRIGGER)

  while (true) {
    const first = yield take(requestChannel)
    yield delay(120)
    const buffered = yield flush(requestChannel)
    const requests = [first, ...buffered].map(action => action.payload || {})

    const idsByBp = {}
    requests.forEach(({ bpId, fileId }) => {
      if (!bpId || !fileId) return
      if (!idsByBp[bpId]) idsByBp[bpId] = new Set()
      idsByBp[bpId].add(fileId)
    })

    const known = yield select(state => state.files.thumbnailsById)

    for (const bpId of Object.keys(idsByBp)) {
      const fileIds = [...idsByBp[bpId]].filter(id => known[id] === undefined)

      for (let i = 0; i < fileIds.length; i += 50) {
        const batch = fileIds.slice(i, i + 50)
        try {
          const response = yield call(fileService.getFileThumbnails, bpId, batch)
          const thumbnails = response?.data?.thumbnails || {}
          const updates = batch.map(fileId => {
            const normalizedFileId = normalizeUuid(fileId)
            const matchingKey = Object.keys(thumbnails).find(key => normalizeUuid(key) === normalizedFileId)
            return { id: fileId, thumbnailUrl: (matchingKey && thumbnails[matchingKey]) || null }
          })
          yield put(updateFileThumbnailsRoutine.success(updates))
        } catch (err) {
          console.error('Failed to fetch visible thumbnails batch:', err)
          yield put(updateFileThumbnailsRoutine.success(batch.map(fileId => ({ id: fileId, thumbnailUrl: null }))))
        }
      }
    }
  }
}

function* getBpFiles({ payload }) {
  const { callback, id, viewable_by_user_id } = payload
  yield put(getBpFilesRoutine.request())
  try {
    if (id) {
      let allFiles = []
      let page = 1
      let hasMoreFiles = true

      while (hasMoreFiles) {
        const queryParams = { page, limit: 3000 }

        // Include viewable_by_user_id if provided
        if (viewable_by_user_id) {
          queryParams.viewable_by_user_id = viewable_by_user_id
        }

        const { data } = yield call(fileService.getFilesListForBp, {
          ...dissoc('callback', payload),
          query: queryParams
        })

        const filesForPage = data?.data || []
        allFiles = [...allFiles, ...filesForPage]

        hasMoreFiles = filesForPage.length === 3000

        // Show files progressively after each page
        yield put(updateLoadingProgressRoutine.success({ key: 'bp', loaded: allFiles.length }))
        const filesWithoutThumbnails = allFiles.map(f => ({ ...f, thumbnailUrl: undefined }))
        yield put(getBpFilesRoutine.success({ files: filesWithoutThumbnails, partial: hasMoreFiles }))

        page++
      }
      typeof callback === 'function' && callback()
    }
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(getBpFilesRoutine.failure(error))
  }
}

export function* getBpFilesCounts({ payload }) {
  const { callback, id } = payload
  yield put(getBpFilesCountsRoutine.request())
  try {
    if (id) {
      const hideDuplicates = (yield select(getCurrentUser))?.hideDuplicateFiles ?? true
      const countsQuery = hideDuplicates ? { 'filter[hide_duplicates]': 1 } : undefined
      const { data } = yield call(fileService.getFilesCounts, { id, query: countsQuery })

      // Normalize byCategory keys: properly reconstruct UUIDs from axios-case-converter mangling
      if (data?.data?.byCategory) {
        const normalizedByCategory = {}
        Object.keys(data.data.byCategory).forEach(key => {
          if (key === 'uncategorised') {
            normalizedByCategory[key] = data.data.byCategory[key]
            return
          }

          // Reconstruct proper UUID format from mangled version
          // axios-case-converter mangles UUIDs by:
          // 1. Converting hyphens to underscores
          // 2. Removing hyphens before capital letters (treating as camelCase)
          // We need to: remove all separators, then reformat as UUID (8-4-4-4-12)

          const cleanedKey = key.replace(/[_-]/g, '').toLowerCase()

          // Check if it's a valid UUID length (32 characters)
          if (cleanedKey.length === 32) {
            // Reconstruct UUID format: XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
            const normalizedKey = [
              cleanedKey.slice(0, 8),
              cleanedKey.slice(8, 12),
              cleanedKey.slice(12, 16),
              cleanedKey.slice(16, 20),
              cleanedKey.slice(20, 32)
            ].join('-')

            normalizedByCategory[normalizedKey] = data.data.byCategory[key]
          } else {
            // Not a valid UUID, keep as-is with underscore replacement
            const normalizedKey = key.replace(/_/g, '-').toLowerCase()
            normalizedByCategory[normalizedKey] = data.data.byCategory[key]
          }
        })
        data.data.byCategory = normalizedByCategory
      }

      yield put(getBpFilesCountsRoutine.success(data?.data ? { ...data.data, bpId: id } : data?.data))
      typeof callback === 'function' && callback(data?.data)
    }
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(getBpFilesCountsRoutine.failure(error))
  }
}

function* getFilesStatistics({ payload }) {
  const { id } = payload
  yield put(getFilesStatisticsRoutine.request())
  try {
    const { data } = yield call(fileService.getFilesStatistics, { id })
    yield put(getFilesStatisticsRoutine.success(data?.data))
  } catch (error) {
    yield put(getFilesStatisticsRoutine.failure(error))
  }
}

// Shared controller for all automatic view sagas (folder, category, all files)
// Ensures switching between ANY load type cancels the previous one
let activeAutoViewController = null

function abortActiveAutoViewLoad() {
  if (activeAutoViewController) {
    activeAutoViewController.abort()
    activeAutoViewController = null
  }
}

function* loadFilesByFolder({ payload }) {
  const { bpId, folderId, fileCategories, cursor = null, callback, search } = payload
  abortActiveAutoViewLoad()
  const abortController = new AbortController()
  activeAutoViewController = abortController
  yield put(loadFilesByFolderRoutine.request({ folderId }))
  try {
    const categoryIds = fileCategories
      .filter(cat => cat.folderId === folderId)
      .map(cat => cat.id)

    if (categoryIds.length === 0) {
      yield put(loadFilesByFolderRoutine.success({ folderId, files: [], nextCursor: null, hasMore: false, replace: true }))
      typeof callback === 'function' && callback([])
      return
    }

    const queryParams = { limit: 50 }
    queryParams.cursor = cursor ?? ''

    categoryIds.forEach((catId, index) => {
      queryParams[`filter[category][${index}]`] = catId
    })

    if (search) queryParams['filter[search]'] = search

    const hideDuplicates = (yield select(getCurrentUser))?.hideDuplicateFiles ?? true
    if (hideDuplicates) {
      queryParams['filter[hide_duplicates]'] = 1
    }

    const { data } = yield call(fileService.getFilesListForBp, {
      id: bpId,
      query: queryParams
    }, abortController.signal)

    const files = (data?.data || []).map(f => ({ ...f, thumbnailUrl: undefined }))

    yield put(loadFilesByFolderRoutine.success({
      folderId,
      files,
      nextCursor: data?.meta?.nextCursor ?? null,
      hasMore: !!data?.meta?.hasMore,
      replace: !cursor
    }))
    typeof callback === 'function' && callback(files)
  } catch (error) {
    if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
      yield put(loadFilesByFolderRoutine.failure({ folderId, error }))
      return
    }
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(loadFilesByFolderRoutine.failure({ folderId, error }))
  } finally {
    if (yield cancelled()) {
      abortController.abort()
      yield put(cancelFileLoadingRoutine.success({ key: folderId, type: 'folder' }))
    }
    // Clear shared controller if it's still ours
    if (activeAutoViewController === abortController) {
      activeAutoViewController = null
    }
  }
}

function* loadFilesByCategory({ payload }) {
  const { bpId, categoryId, cursor = null, callback, search } = payload
  abortActiveAutoViewLoad()
  const abortController = new AbortController()
  activeAutoViewController = abortController
  yield put(loadFilesByCategoryRoutine.request({ categoryId }))
  try {
    const queryParams = { limit: 50 }
    queryParams.cursor = cursor ?? ''

    if (categoryId === 'uncategorised') {
      queryParams['filter[uncategorised]'] = 'true'
    } else {
      queryParams['filter[category]'] = categoryId
    }

    if (search) queryParams['filter[search]'] = search

    const hideDuplicates = (yield select(getCurrentUser))?.hideDuplicateFiles ?? true
    if (hideDuplicates) {
      queryParams['filter[hide_duplicates]'] = 1
    }

    const { data } = yield call(fileService.getFilesListForBp, {
      id: bpId,
      query: queryParams
    }, abortController.signal)

    const files = (data?.data || []).map(f => ({ ...f, thumbnailUrl: undefined }))

    yield put(loadFilesByCategoryRoutine.success({
      categoryId,
      files,
      nextCursor: data?.meta?.nextCursor ?? null,
      hasMore: !!data?.meta?.hasMore,
      replace: !cursor
    }))
    typeof callback === 'function' && callback(files)
  } catch (error) {
    if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
      yield put(loadFilesByCategoryRoutine.failure({ categoryId, error }))
      return
    }
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(loadFilesByCategoryRoutine.failure({ categoryId, error }))
  } finally {
    if (yield cancelled()) {
      abortController.abort()
      yield put(cancelFileLoadingRoutine.success({ key: categoryId, type: 'category' }))
    }
    if (activeAutoViewController === abortController) {
      activeAutoViewController = null
    }
  }
}

function* loadAllFiles({ payload }) {
  const { bpId, viewable_by_user_id, cursor = null, callback, search } = payload
  abortActiveAutoViewLoad()
  const abortController = new AbortController()
  activeAutoViewController = abortController
  yield put(loadAllFilesRoutine.request())
  try {
    const queryParams = { limit: 50 }
    queryParams.cursor = cursor ?? ''

    if (viewable_by_user_id) {
      queryParams.viewable_by_user_id = viewable_by_user_id
    }

    if (search) queryParams['filter[search]'] = search

    const hideDuplicates = (yield select(getCurrentUser))?.hideDuplicateFiles ?? true
    if (hideDuplicates) {
      queryParams['filter[hide_duplicates]'] = 1
    }

    const { data } = yield call(fileService.getFilesListForBp, {
      id: bpId,
      query: queryParams
    }, abortController.signal)

    const files = (data?.data || []).map(f => ({ ...f, thumbnailUrl: undefined }))

    yield put(loadAllFilesRoutine.success({
      files,
      nextCursor: data?.meta?.nextCursor ?? null,
      hasMore: !!data?.meta?.hasMore,
      replace: !cursor
    }))
    typeof callback === 'function' && callback(files)
  } catch (error) {
    if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
      yield put(loadAllFilesRoutine.failure(error))
      return
    }
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(loadAllFilesRoutine.failure(error))
  } finally {
    if (yield cancelled()) {
      abortController.abort()
      yield put(cancelFileLoadingRoutine.success({ key: 'all', type: 'all' }))
    }
    if (activeAutoViewController === abortController) {
      activeAutoViewController = null
    }
  }
}

// Module-level map for custom folder AbortControllers (takeEvery doesn't support cancelled())
const activeCustomFolderControllers = new Map()

function* loadCustomFolderFiles({ payload }) {
  const { bpId, customViewId, customFolderId, cancelOthers, callback } = payload
  const folderKey = customFolderId || 'unorganized'

  // When user explicitly selects a folder, cancel all other in-flight loads
  if (cancelOthers) {
    activeCustomFolderControllers.forEach((controller, key) => {
      if (key !== folderKey) controller.abort()
    })
  }

  // Cancel any existing load for this SAME folder
  if (activeCustomFolderControllers.has(folderKey)) {
    activeCustomFolderControllers.get(folderKey).abort()
  }
  const abortController = new AbortController()
  activeCustomFolderControllers.set(folderKey, abortController)

  yield put(loadCustomFolderFilesRoutine.request({ customFolderId: folderKey }))
  try {
    let allFiles = []
    let page = 1
    let hasMoreFiles = true

    while (hasMoreFiles) {
      let response

      if (customFolderId === null) {
        // Load unassigned files using custom view files endpoint (no folder filter)
        const queryParams = { page, limit: 3000 }
        response = yield call(fileService.getCustomViewFiles, {
          bpId,
          customViewId,
          query: queryParams
        }, abortController.signal)
      } else {
        // Load files for specific folder
        const queryParams = { page, limit: 3000 }
        response = yield call(fileService.getCustomFolderFiles, {
          bpId,
          customViewId,
          customFolderId,
          query: queryParams
        }, abortController.signal)
      }

      const filesForPage = response?.data?.data || []
      allFiles = [...allFiles, ...filesForPage]

      hasMoreFiles = filesForPage.length === 3000

      // Dispatch progress after each page and show files progressively
      yield put(updateLoadingProgressRoutine.success({ key: folderKey, loaded: allFiles.length }))
      const filesWithoutThumbnails = allFiles.map(f => ({ ...f, thumbnailUrl: undefined }))
      yield put(loadCustomFolderFilesRoutine.success({ customFolderId: folderKey, files: filesWithoutThumbnails, partial: hasMoreFiles }))

      page++
    }
    typeof callback === 'function' && callback(allFiles)
  } catch (error) {
    if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
      // Clean up loading state on cancellation
      yield put(loadCustomFolderFilesRoutine.failure({ customFolderId: folderKey, error }))
      return
    }
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(loadCustomFolderFilesRoutine.failure({ customFolderId: folderKey, error }))
  } finally {
    activeCustomFolderControllers.delete(folderKey)
  }
}

function* cancelAllCustomFolderLoads() {
  activeCustomFolderControllers.forEach(controller => controller.abort())
  activeCustomFolderControllers.clear()
}

export function* cancelAllCustomFolderLoadsWatcher() {
  yield takeLatest(invalidateCustomFolderCacheRoutine.TRIGGER, cancelAllCustomFolderLoads)
}

function* cancelAutoViewLoading() {
  abortActiveAutoViewLoad()
}

export function* cancelAutoViewLoadingWatcher() {
  yield takeLatest(cancelAutoViewLoadingRoutine.TRIGGER, cancelAutoViewLoading)
}

function* getRecentFiles({ payload }) {
  const { callback, id } = payload
  yield put(getRecentFilesRoutine.request())
  try {
    if (id) {
      const { data } = yield call(fileService.getRecentFiles, {
        ...dissoc('callback', payload)
      })

      const recentFiles = data?.data || []

      yield put(getRecentFilesRoutine.success(recentFiles))
      typeof callback === 'function' && callback()
    }
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(getRecentFilesRoutine.failure(error))
  }
}
function* getUploadProcess({ payload }) {
  yield put(getUploadProcessRoutine.request())
  try {
    if (isNotNilOrEmpty(payload.unitId)) {
      const { data } = yield call(
        fileService.getUnitMultiuploadProcessProgress,
        payload
      )
      yield put(getUploadProcessRoutine.success(data.data))
    } else {
      const { data } = yield call(
        fileService.getMultiuploadProcessProgress,
        payload
      )
      yield put(getUploadProcessRoutine.success(data.data))
    }
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(getUploadProcessRoutine.failure(error))
  }
}

function* clearBpFiles() {
  yield put(clearBpFilesRoutine.success())
}

function* getFileTypes() {
  yield put(getFileTypesRoutine.request())
  try {
    const result = yield call(bpService.getFileTypes)
    yield put(getFileTypesRoutine.success(result.data.data))
  } catch (error) {
    yield put(getFileTypesRoutine.failure(error))
  }
}

export function* updateFile({ payload }) {
  const { clearValues } = payload
  yield put(updateFileRoutine.request())
  try {
    const response = yield call(bpService.updateFile, payload)
    const updatedFile = pathOr(null, ['data', 'data'], response)
    yield put(updateFileRoutine.success({ id: payload.id, body: payload.body, file: updatedFile?.id ? updatedFile : null }))
    toast.success(fileUpdateMessage(payload.body, payload.previousName))
    typeof clearValues === 'function' && clearValues()
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(updateFileRoutine.failure(error))
  }
}

export function* finalizeFile({ payload }) {
  yield put(finalizeFileRoutine.request())
  try {
    const response = yield call(fileService.finalizeMultiUploadProcess, payload)
    // Optimistic update: add the finalized file to the list if returned
    const finalizedFile = pathOr(null, ['data', 'data'], response)
    yield put(finalizeFileRoutine.success({ file: finalizedFile }))

    // If no file data in response, fallback to refetch
    if (!finalizedFile) {
      yield put(getBpFilesRoutine({ id: payload.bpId }))
    }
    if (payload.bpId) yield put(getBpFilesCountsRoutine({ id: payload.bpId }))
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(finalizeFileRoutine.failure(error))
  }
}

function* shareFile({ payload }) {
  yield put(shareFileRoutine.request())
  try {
    const response = yield call(bpService.shareFileWithUnits, payload)
    // Optimistic update: use the updated file from the response if available
    const updatedFile = pathOr(null, ['data', 'data'], response)
    yield put(shareFileRoutine.success({ file: updatedFile }))

    // If no file data in response, fallback to refetch
    if (!updatedFile) {
      yield put(getBpFilesRoutine({ id: payload.bpId }))
    }

    isEmpty(payload.buildingUnitPassportsIds)
      ? toast.success('File has been unshared')
      : toast.success('File has been shared')
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(shareFileRoutine.failure(error))
  }
}

function* getSharedUnitsListForFile({ payload }) {
  yield put(getSharedUnitsListForFileRoutine.request())
  try {
    const { data } = yield call(bpService.getSharedUnitsListForFile, payload)
    yield put(getSharedUnitsListForFileRoutine.success(data.data))
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(getSharedUnitsListForFileRoutine.failure(error))
  }
}

function* clearSharedUnitsListForFile() {
  yield put(clearSharedUnitsListForFileRoutine.success())
}

function* downloadFile({ payload }) {
  yield put(downloadFileRoutine.request())
  try {
    const anchor = document.createElement('a')
    anchor.href =
      process.env.REACT_APP_API_URL +
      `/building-passport/files/${payload}/download`
    anchor.target = '_blank'
    anchor.download = 'file.pdf'
    anchor.click()
    yield put(downloadFileRoutine.success())
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(downloadFileRoutine.failure(error))
  }
}

function* saveUploadProcessId({ payload }) {
  yield put(saveUploadProcessFetchDetailsRoutine.success(payload))
}

function* clearUploadProcessId() {
  yield put(clearUploadProcessFetchDetailsRoutine.success())
}




function* getFileTagsForBp({ payload }) {
  yield put(getFileTagsForBpRoutine.request())
  try {
    const { data } = yield call(fileService.getTagsListForBp, payload)
    yield put(getFileTagsForBpRoutine.success(data.data))
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(getFileTagsForBpRoutine.failure(error))
  }
}

function* createFileTag({ payload }) {
  const { callback } = payload
  yield put(createFileTagRoutine.request())
  try {
    const { data } = yield call(
      fileService.createFileTag,
      dissoc('callback', payload)
    )
    yield put(createFileTagRoutine.success(data.data))
    toast.success('Tag created successfully')

    typeof callback === 'function' && callback()
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(createFileTagRoutine.failure(error))
  }
}

function* removeFileTag({ payload }) {
  yield put(removeFileTagRoutine.request())
  try {
    const { data } = yield call(fileService.removeFileTag, payload)
    yield put(removeFileTagRoutine.success(data.data))
    toast.success('Tag deleted successfully')

    // Refetch tags list since a tag was deleted
    yield put(getFileTagsForBpRoutine({ bpId: payload.bpId }))

    // Optimistic update: remove the tag from all files
    // The response should contain updated files or we can manually remove the tag
    const currentFiles = yield select(state => state.files.bpFilesList)
    const updatedFiles = currentFiles.map(file => {
      if (file.fileTags && Array.isArray(file.fileTags)) {
        return {
          ...file,
          fileTags: file.fileTags.filter(tag => tag.id !== payload.id)
        }
      }
      return file
    })
    yield put(getBpFilesRoutine.success(updatedFiles))
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(removeFileTagRoutine.failure(error))
  }
}

function* attachFileTagToFile({ payload }) {
  const { callback } = payload
  yield put(attachFileTagToFileRoutine.request())
  try {
    const { data } = yield call(fileService.attachFileTagToFile, payload)
    yield put(attachFileTagToFileRoutine.success(data.data))
    toast.success('Tag attached to file successfully')

    typeof callback === 'function' && callback()
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(attachFileTagToFileRoutine.failure(error))
  }
}

function* syncFileTagsInFile({ payload }) {
  const { callback, newTags } = payload
  yield put(syncFileTagsInFileRoutine.request())
  try {
    const { data } = yield call(fileService.syncFileTags, payload)
    yield put(syncFileTagsInFileRoutine.success(data.data))
    toast.success('File tags updated successfully')

    if (data.isTagDeleted || isNotNilOrEmpty(newTags)) {
      yield put(getFileTagsForBpRoutine({ bpId: payload.bpId }))
    }

    typeof callback === 'function' && callback()
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(syncFileTagsInFileRoutine.failure(error))
  }
}

function* syncFileTagsInFileVersion({ payload }) {
  const { callback, newTags } = payload
  yield put(syncFileTagsInFileVersionRoutine.request())
  try {
    const { data } = yield call(fileService.syncFileVersionTags, payload)
    yield put(syncFileTagsInFileVersionRoutine.success(data.data))
    toast.success('File version tags updated successfully')

    if (data.isTagDeleted || isNotNilOrEmpty(newTags)) {
      yield put(getFileTagsForBpRoutine({ bpId: payload.bpId }))
    }

    typeof callback === 'function' && callback()
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(syncFileTagsInFileVersionRoutine.failure(error))
  }
}

function* detachFileTagFromFile({ payload }) {
  yield put(detachFileTagFromFileRoutine.request())
  try {
    const { data } = yield call(fileService.detachFileTagFromFile, payload)
    yield put(detachFileTagFromFileRoutine.success(data.data))
    toast.success('Tag removed from file successfully')

    if (data.isTagDeleted) {
      yield put(getFileTagsForBpRoutine({ bpId: payload.bpId }))
    }
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(detachFileTagFromFileRoutine.failure(error))
  }
}

function* clearUploadProcess() {
  yield put(clearUploadProcessRoutine.success())
}

export function* reclassifyFile({ payload }) {
  const { fileId, fileTypeId, callback } = payload
  yield put(reclassifyFileRoutine.request())
  try {
    const response = yield call(fileService.reclassifyFile, { fileId, fileTypeId })
    // Optimistic update: use the updated file from the response if available
    const updatedFile = pathOr(null, ['data', 'data'], response)
    yield put(reclassifyFileRoutine.success({ file: updatedFile }))

    // If no file data in response, fallback to refetch
    if (!updatedFile) {
      yield put(getBpFilesRoutine({ id: payload.bpId }))
    }
    if (payload.bpId) yield put(getBpFilesCountsRoutine({ id: payload.bpId }))

    toast.success('File has been reclassified successfully.')
    if (callback) {
      callback()
    }
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(reclassifyFileRoutine.failure(error))
  }
}

function* getRecommendedFileTypes({ payload }) {
  yield put(getRecommendedFileTypesRoutine.request())
  try {
    const { data } = yield call(fileService.getRecommendedFileTypes, payload)
    yield put(getRecommendedFileTypesRoutine.success({
      list: data.recommendations,
      message: data.message || null
    }))
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(getRecommendedFileTypesRoutine.failure(error))
  }
}

export function* getFileTagsForBpWatcher() {
  yield takeLatest(getFileTagsForBpRoutine.TRIGGER, getFileTagsForBp)
}
export function* createFileTagWatcher() {
  yield takeLatest(createFileTagRoutine.TRIGGER, createFileTag)
}
export function* removeFileTagWatcher() {
  yield takeLatest(removeFileTagRoutine.TRIGGER, removeFileTag)
}
export function* attachFileTagToFileWatcher() {
  yield takeLatest(attachFileTagToFileRoutine.TRIGGER, attachFileTagToFile)
}
export function* syncFileTagsInFileWatcher() {
  yield takeLatest(syncFileTagsInFileRoutine.TRIGGER, syncFileTagsInFile)
}

export function* syncFileTagsInFileVersionWatcher() {
  yield takeLatest(
    syncFileTagsInFileVersionRoutine.TRIGGER,
    syncFileTagsInFileVersion
  )
}
export function* detachFileTagFromFileWatcher() {
  yield takeLatest(detachFileTagFromFileRoutine.TRIGGER, detachFileTagFromFile)
}

export function* getFileTypesWatcher() {
  yield takeLatest(getFileTypesRoutine.TRIGGER, getFileTypes)
}

export function* removeFileWatcher() {
  yield takeEvery(removeFileRoutine.TRIGGER, removeFile)
}

export function* restoreFileWatcher() {
  yield takeLatest(restoreFileRoutine.TRIGGER, restoreFile)
}

export function* getBpFilesWatcher() {
  yield takeLatest(getBpFilesRoutine.TRIGGER, getBpFiles)
}

export function* getBpFilesCountsWatcher() {
  yield takeLatest(getBpFilesCountsRoutine.TRIGGER, getBpFilesCounts)
}

export function* getFilesStatisticsWatcher() {
  yield takeLatest(getFilesStatisticsRoutine.TRIGGER, getFilesStatistics)
}

export function* loadFilesByFolderWatcher() {
  yield takeLatest(loadFilesByFolderRoutine.TRIGGER, loadFilesByFolder)
}

export function* loadFilesByCategoryWatcher() {
  yield takeLatest(loadFilesByCategoryRoutine.TRIGGER, loadFilesByCategory)
}

export function* loadAllFilesWatcher() {
  yield takeLatest(loadAllFilesRoutine.TRIGGER, loadAllFiles)
}

export function* loadCustomFolderFilesWatcher() {
  // Use takeEvery instead of takeLatest to allow multiple folder loads in parallel
  // This is important when moving files - we need to reload both source AND destination folders
  yield takeEvery(loadCustomFolderFilesRoutine.TRIGGER, loadCustomFolderFiles)
}

export function* getRecentFilesWatcher() {
  yield takeLatest(getRecentFilesRoutine.TRIGGER, getRecentFiles)
}

export function* clearBpFilesWatcher() {
  yield takeLatest(clearBpFilesRoutine.TRIGGER, clearBpFiles)
}

export function* updateFileWatcher() {
  yield takeEvery(updateFileRoutine.TRIGGER, updateFile)
}

export function* finalizeFileWatcher() {
  yield takeEvery(finalizeFileRoutine.TRIGGER, finalizeFile)
}

export function* shareFileWatcher() {
  yield takeLatest(shareFileRoutine.TRIGGER, shareFile)
}

export function* downloadFileWatcher() {
  yield takeLatest(downloadFileRoutine.TRIGGER, downloadFile)
}

export function* getSharedUnitsListForFileWatcher() {
  yield takeLatest(
    getSharedUnitsListForFileRoutine.TRIGGER,
    getSharedUnitsListForFile
  )
}

export function* clearSharedUnitsListForFileWatcher() {
  yield takeLatest(
    clearSharedUnitsListForFileRoutine.TRIGGER,
    clearSharedUnitsListForFile
  )
}

export function* uploadFileVersionWatcher() {
  yield takeLatest(addFileVersionRoutine.TRIGGER, uploadFileVersion)
}


export function* saveUploadProcessIdWatcher() {
  yield takeLatest(
    saveUploadProcessFetchDetailsRoutine.TRIGGER,
    saveUploadProcessId
  )
}

export function* clearUploadProcessIdWatcher() {
  yield takeLatest(
    clearUploadProcessFetchDetailsRoutine.TRIGGER,
    clearUploadProcessId
  )
}

export function* clearUploadProcessWatcher() {
  yield takeLatest(clearUploadProcessRoutine.TRIGGER, clearUploadProcess)
}

export function* getUploadProcessWatcher() {
  yield takeLatest(getUploadProcessRoutine.TRIGGER, getUploadProcess)
}

export function* reclassifyFileWatcher() {
  yield takeLatest(reclassifyFileRoutine.TRIGGER, reclassifyFile)
}

export function* getRecommendedFileTypesWatcher() {
  yield takeLatest(
    getRecommendedFileTypesRoutine.TRIGGER,
    getRecommendedFileTypes
  )
}

export function* extendSessionWatcher() {
  yield takeLatest(extendSessionRoutine.TRIGGER, extendSessionSaga)
}

export const filesSagas = [
  fork(getFileTagsForBpWatcher),
  fork(createFileTagWatcher),
  fork(removeFileTagWatcher),
  fork(attachFileTagToFileWatcher),
  fork(syncFileTagsInFileWatcher),
  fork(syncFileTagsInFileVersionWatcher),
  fork(detachFileTagFromFileWatcher),
  fork(updateFileWatcher),
  fork(finalizeFileWatcher),
  fork(removeFileWatcher),
  fork(restoreFileWatcher),
  fork(getBpFilesWatcher),
  fork(getBpFilesCountsWatcher),
  fork(getFilesStatisticsWatcher),
  fork(loadFilesByFolderWatcher),
  fork(loadFilesByCategoryWatcher),
  fork(loadAllFilesWatcher),
  fork(loadCustomFolderFilesWatcher),
  fork(cancelAllCustomFolderLoadsWatcher),
  fork(cancelAutoViewLoadingWatcher),
  fork(clearBpFilesWatcher),
  fork(getFileTypesWatcher),
  fork(downloadFileWatcher),
  fork(getSharedUnitsListForFileWatcher),
  fork(clearSharedUnitsListForFileWatcher),
  fork(shareFileWatcher),
  fork(uploadFileVersionWatcher),
  fork(saveUploadProcessIdWatcher),
  fork(clearUploadProcessIdWatcher),
  fork(clearUploadProcessWatcher),
  fork(getUploadProcessWatcher),
  fork(getRecentFilesWatcher),
  fork(watchVisibleThumbnailRequests),
  fork(reclassifyFileWatcher),
  fork(getRecommendedFileTypesWatcher),
  fork(extendSessionWatcher)
]
